import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";
import {Chart} from "primereact/chart";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {setMessage} from "../../../system/app-slice";
import {projectDashboardApi} from "../../../models/project/project-dashboard-api";
import {
    GOAL_STATUS,
    HEALTH_STATUS,
    MILESTONE_STATUS,
    READINESS_LABELS,
    FORECAST_REASONS,
    RISK_STATUS,
    SEVERITY,
    attentionTitle,
    daysText,
    fullDate,
    healthSentence,
    progressMethodText,
    shortDate,
    signalsSummary,
    whenText,
} from "./dashboard-labels";
import {
    AllocationDialog,
    AttentionDrawer,
    FollowUpDialog,
    HelpTip,
    RescheduleDialog,
    RiskReasonDrawer,
    TimelineDrawer,
} from "./dashboard-panels";
import "./project-dashboard.css";

// «مرکز فرمان پروژه» — the project dashboard tab (boss's task «تب داشبورد پروژه», 2026-10-22; spec files «آموزش بخش
// داشبورد پروژه» and «منطق معماری at risk»; docs/tasks/22-project-dashboard.md). Facts and analysis are kept apart:
// figures come from recorded data, «در خطر» is the system's analysis and always shows its evidence.

const VIEW_KEY = "zofino.project-dashboard.view";
const CHART_FONT = "Shabnam-FD, IranYekan, Tahoma, sans-serif";
// calendar days from today (due tomorrow = 1, even if less than 24 hours away)
const startOfDay = (value) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
};
export const daysUntil = (value, now = Date.now()) => (value ? Math.round((startOfDay(value) - startOfDay(now)) / 86400000) : null);

function readView() {
    try {
        return window.localStorage.getItem(VIEW_KEY);
    } catch (error) {
        return null;
    }
}

export default function ProjectDashboard({projectData, updateTask, projectAccess}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const {workspaceMembershipData} = useSelector((state) => state.app);
    const members = workspaceMembershipData?.workspace?.workspace_memberships || [];
    const meId = workspaceMembershipData?.id;
    const projectKey = projectData?.documentId;

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [range, setRange] = useState("30");
    const [view, setView] = useState(() => readView());
    const [why, setWhy] = useState(null);
    const [followUpFor, setFollowUpFor] = useState(null);
    const [attentionOpen, setAttentionOpen] = useState(false);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [allocationFor, setAllocationFor] = useState(undefined);

    const load = useCallback(async (nextRange = range) => {
        if (!projectKey) return;
        setFailed(false);
        try {
            setDashboard(await projectDashboardApi.get(projectKey, nextRange));
        } catch (error) {
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [projectKey, range]);

    useEffect(() => {
        setLoading(true);
        load(range);
        // reload when the project or the chart range changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectKey, range]);

    // A milestone added, archived or removed anywhere (the identity tab, the setup bar, a sidebar) changes this page's
    // milestones, outputs and risks, but the page only loaded when it opened: the new milestone showed after leaving
    // the project and coming back (boss's bug report 2026-10-29). Reload quietly when the set of milestones changes.
    const milestoneSignature = (projectData?.deliverables || []).filter((item) => !item?.archivedAt).map((item) => item?.documentId ?? item?.id).sort().join(",");
    const lastMilestones = useRef({projectKey, milestoneSignature});
    useEffect(() => {
        const last = lastMilestones.current;
        lastMilestones.current = {projectKey, milestoneSignature};
        if (last.projectKey !== projectKey || last.milestoneSignature === milestoneSignature) return;
        load();
        // only the milestone set matters here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectKey, milestoneSignature]);

    const canManage = Boolean(dashboard?.permissions?.canManage);
    const activeView = view || (canManage ? "manager" : "member");
    const chooseView = (next) => {
        setView(next);
        try {
            window.localStorage.setItem(VIEW_KEY, next);
        } catch (error) {
            // a per-viewer convenience only
        }
    };
    const canEditTasks = Boolean(projectAccess?.canUpdateTask) && !projectAccess?.isReadOnly;
    const canActOn = (item) => canManage || (item?.entityType === "task" && item?.task?.assignee?.id === meId);

    const openEntity = (item) => {
        const params = new URLSearchParams(location.search);
        if (item.entityType === "task") params.set("task", item.entityId);
        else if (item.entityType === "milestone") params.set("deliverable", item.entityId);
        else return;
        navigate(`${location.pathname}?${params.toString()}`);
    };
    const openTask = (documentId) => openEntity({entityType: "task", entityId: documentId});

    const review = async (item) => {
        try {
            await projectDashboardApi.review(projectKey, item);
            setWhy(null);
            dispatch(setMessage({severity: "success", content: "هشدار بررسی شد؛ اگر وضعیت بدتر شود دوباره نمایش داده می‌شود."}));
            load();
        } catch (error) {
            // the global error handler shows the message
        }
    };
    const createFollowUp = async (followUp) => {
        await projectDashboardApi.createFollowUp(projectKey, followUp);
        setFollowUpFor(null);
        dispatch(setMessage({severity: "success", content: "پیگیری ثبت شد و به مسئول آن اعلان ارسال می‌شود."}));
        load();
    };
    const closeFollowUp = async (followUp) => {
        await projectDashboardApi.closeFollowUp(projectKey, followUp.documentId, "done");
        load();
    };
    const membershipOf = (documentId) => members.find((membership) => (membership.documentId || membership.id) === documentId);
    const reschedule = async (rows) => {
        for (const {task, change} of rows) {
            const update = {};
            if (change.dueDate) update.dueDate = new Date(change.dueDate * 1000);
            if (change.assignee && change.assignee !== task.assignee?.documentId) update.assignee = membershipOf(change.assignee);
            if (Object.keys(update).length) await updateTask({documentId: task.documentId}, update);
        }
        setRescheduleOpen(false);
        dispatch(setMessage({severity: "success", content: "برنامه جدید ذخیره شد."}));
        load();
    };
    const reassign = async (tasks, toDocumentId) => {
        const membership = membershipOf(toDocumentId);
        for (const task of tasks) await updateTask({documentId: task.documentId}, {assignee: membership});
        setAllocationFor(undefined);
        dispatch(setMessage({severity: "success", content: "کارهای انتخاب‌شده واگذار شد."}));
        load();
    };

    if (loading && !dashboard) {
        return <div className="pd-root flex align-items-center justify-content-center"><i className="pi pi-spin pi-spinner text-2xl text-400"/></div>;
    }
    if (failed && !dashboard) {
        return (
            <div className="pd-root flex flex-column align-items-center justify-content-center gap-2">
                <span className="text-sm text-600">داشبورد پروژه بارگذاری نشد.</span>
                <button type="button" className="pd-btn" onClick={() => { setLoading(true); load(); }}>تلاش دوباره</button>
            </div>
        );
    }

    const health = HEALTH_STATUS[dashboard.health.status];
    const attention = dashboard.attention.items;

    return (
        <div className="pd-root" dir="rtl">
            <div className="pd-container">
                {/* title and view */}
                <div className="flex flex-column lg:flex-row lg:align-items-center justify-content-between gap-3">
                    <div>
                        <p className="pd-label m-0">امروز چه چیزی مهم است؟</p>
                        <h2 className="text-xl font-bold m-0 mt-1">مرکز فرمان پروژه</h2>
                    </div>
                    <div className="flex align-items-center gap-2">
                        {canManage && (
                            <button type="button" className="pd-btn" onClick={() => navigate(`/projects/${projectKey}/update-status`)}>
                                <Icon icon="mi mi:campaign" className="text-lg"/>ثبت بروزرسانی
                            </button>
                        )}
                        <span className="pd-tiny text-500">نمایش:</span>
                        <div className="pd-view-switch">
                            <button type="button" className={activeView === "manager" ? "is-active" : ""} onClick={() => chooseView("manager")}>مدیریتی</button>
                            <button type="button" className={activeView === "member" ? "is-active" : ""} onClick={() => chooseView("member")}>کار من</button>
                        </div>
                    </div>
                </div>

                <CommandStrip dashboard={dashboard} health={health} attentionCount={attention.length}
                              onAttention={() => setAttentionOpen(true)} onTimeline={() => setTimelineOpen(true)}/>

                {activeView === "manager" ? (
                    <ManagerView
                        dashboard={dashboard}
                        range={range}
                        onRange={setRange}
                        onWhy={setWhy}
                        onFollowUp={setFollowUpFor}
                        canActOn={canActOn}
                        canEditTasks={canEditTasks}
                        onReschedule={() => setRescheduleOpen(true)}
                        onAllocation={(documentId) => setAllocationFor(documentId || null)}
                        onOpenMilestone={(documentId) => openEntity({entityType: "milestone", entityId: documentId})}
                        onOpenAttention={() => setAttentionOpen(true)}
                        onOpenTimeline={() => setTimelineOpen(true)}
                        navigate={navigate}
                    />
                ) : (
                    <MemberView dashboard={dashboard} onOpenTask={openTask} onWhy={setWhy} onCloseFollowUp={closeFollowUp}/>
                )}
            </div>

            <RiskReasonDrawer
                item={why}
                visible={Boolean(why)}
                onHide={() => setWhy(null)}
                canAct={canActOn(why)}
                onFollowUp={(item) => { setWhy(null); setFollowUpFor(item); }}
                onReview={review}
                onOpen={why?.entityType === "task" || why?.entityType === "milestone" ? (item) => { setWhy(null); openEntity(item); } : null}
            />
            <AttentionDrawer
                visible={attentionOpen}
                onHide={() => setAttentionOpen(false)}
                items={attention}
                reviewedCount={dashboard.attention.reviewedCount}
                canActOn={canActOn}
                onWhy={(item) => { setAttentionOpen(false); setWhy(item); }}
                onFollowUp={(item) => { setAttentionOpen(false); setFollowUpFor(item); }}
            />
            <TimelineDrawer
                visible={timelineOpen}
                onHide={() => setTimelineOpen(false)}
                dashboard={dashboard}
                canReschedule={canEditTasks}
                onReschedule={() => { setTimelineOpen(false); setRescheduleOpen(true); }}
                onWhyTask={(documentId) => {
                    const item = attention.find((entry) => entry.entityType === "task" && entry.entityId === documentId);
                    setTimelineOpen(false);
                    if (item) setWhy(item);
                    else openTask(documentId);
                }}
            />
            <FollowUpDialog
                item={followUpFor}
                visible={Boolean(followUpFor)}
                onHide={() => setFollowUpFor(null)}
                members={members}
                defaultOwner={followUpFor?.task?.assignee?.documentId || dashboard.project.owner?.documentId || null}
                onSubmit={createFollowUp}
            />
            <RescheduleDialog visible={rescheduleOpen} onHide={() => setRescheduleOpen(false)} tasks={dashboard.overdueTasks} members={members} onSave={reschedule}/>
            <AllocationDialog
                visible={allocationFor !== undefined}
                onHide={() => setAllocationFor(undefined)}
                team={dashboard.team}
                focus={allocationFor}
                openTasks={dashboard.openTasks || []}
                members={members}
                onSave={reassign}
            />
        </div>
    );
}

// ---------- top strip: status, summary and the current prediction ----------
function CommandStrip({dashboard, health, attentionCount, onAttention, onTimeline}) {
    const {progress, forecast} = dashboard;
    const gap = progress.gap;
    return (
        <section className="pd-card soft pd-strip">
            <div className="pd-card-pad">
                <div className="flex align-items-start gap-3">
                    <div className={`pd-icon-box pd-bg tone-${health.tone}`}><Icon icon={`mi mi:${health.icon}`} className={`tone-${health.tone}`}/></div>
                    <div className="flex-1">
                        <div className="flex flex-wrap align-items-center gap-2">
                            <h3 className="text-lg font-bold m-0">{health.headline}</h3>
                            <span className={`pd-pill tone-${health.tone}`}>{`سلامت: ${health.label}`}</span>
                            <HelpTip text="جمع‌بندی زمان، پیشرفت، مایلستون‌ها و کارهای در خطر. «در خطر» یعنی نشانه‌هایی دیده شده که ارزش بررسی دارند؛ به معنی شکست یا توقف نیست."/>
                        </div>
                        <p className="text-xs text-600 line-height-3 mt-2 mb-0">{healthSentence(dashboard)}</p>
                        <div className="flex flex-wrap align-items-center gap-2 mt-3">
                            <button type="button" className={`pd-btn ${attentionCount ? "primary" : ""}`} onClick={onAttention}>
                                {attentionCount ? `${attentionCount} مورد نیازمند توجه` : "موردی نیازمند توجه نیست"}
                            </button>
                            <button type="button" className="pd-btn" onClick={onTimeline}>مشاهده برنامه زمانی</button>
                            <HelpTip text="برنامه زمانی پروژه را نشان می‌دهد: از شروع تا تحویل، مایلستون‌ها، کارهای در خطر و پیش‌بینی تحویل اگر داده کافی وجود داشته باشد."/>
                        </div>
                    </div>
                </div>
            </div>
            <div className="pd-strip-side">
                <p className="pd-tiny text-500 m-0 mb-3">پیش‌بینی فعلی</p>
                <div className="pd-strip-figures">
                    <div>
                        <span className="pd-label">پیشرفت</span>
                        <div className="pd-big">{`${progress.actual}٪`}</div>
                        <span className={`pd-tiny ${gap > 0 ? "tone-red" : "tone-green"}`}>
                            {gap === null ? "برنامه کافی ثبت نشده" : gap > 0 ? `${gap}٪ عقب‌تر` : gap < 0 ? `${-gap}٪ جلوتر` : "طبق برنامه"}
                        </span>
                    </div>
                    <div>
                        <span className="pd-label">تحویل</span>
                        <div className="text-sm font-bold mt-2">{forecast.available ? shortDate(forecast.at) : shortDate(dashboard.project.dueDate) || "—"}</div>
                        <span className={`pd-tiny ${forecast.available && forecast.delayDays > 0 ? "tone-amber" : "text-500"}`}>
                            {forecast.available
                                ? (forecast.delayDays > 0 ? `+${forecast.delayDays} روز` : forecast.delayDays !== null ? "در موعد" : "پیش‌بینی")
                                : dashboard.project.dueDate ? "موعد برنامه" : "بدون موعد"}
                        </span>
                    </div>
                    <div>
                        <span className="pd-label flex align-items-center gap-1">اعتماد <HelpTip text="چقدر به این تحلیل اعتماد داریم: سهم کارهای باز با موعد، برآورد زمان و مسئول، و تعداد کارهای تمام‌شده اخیر."/></span>
                        <div className="pd-big">{`${forecast.confidence}٪`}</div>
                        <span className="pd-tiny text-500">{forecast.available ? "داده کافی" : FORECAST_REASONS[forecast.reasons?.[0]] || "داده ناکافی"}</span>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ---------- manager view ----------
function ManagerView({dashboard, range, onRange, onWhy, onFollowUp, canActOn, canEditTasks, onReschedule, onAllocation, onOpenMilestone, onOpenAttention, onOpenTimeline, navigate}) {
    const {kpis} = dashboard;
    const next = kpis.nextMilestone;
    const nextDays = next ? daysUntil(next.dueAt) : null;
    return (
        <>
            <section className="pd-kpis">
                <button type="button" className="pd-card pd-kpi" onClick={canEditTasks && kpis.overdueTasks ? onReschedule : onOpenTimeline}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">کارهای باز</span><Icon icon="mi mi:checklist" className="text-400"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{kpis.openTasks}</span>
                        <span className={`pd-tiny mb-1 ${kpis.overdueTasks ? "tone-red" : "text-500"}`}>{kpis.overdueTasks ? `${kpis.overdueTasks} عقب‌افتاده` : `از ${kpis.totalTasks} کار`}</span></div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={() => (next ? onOpenMilestone(next.documentId) : onOpenTimeline())}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">مایلستون بعدی</span><Icon icon="mi mi:flag" className="text-400"/></div>
                    <div className="mt-2">
                        <span className="text-sm font-bold">{next ? next.title : "مایلستونی ثبت نشده"}</span>
                        {next && <span className={`block pd-tiny mt-1 ${next.risk !== "normal" ? `tone-${RISK_STATUS[next.risk].tone}` : "text-500"}`}>
                            {`${nextDays !== null ? daysText(nextDays) : "بدون موعد"} · ${next.risk !== "normal" ? RISK_STATUS[next.risk].label : `${next.percent}٪`}`}
                        </span>}
                    </div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={onOpenAttention}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">موارد در خطر</span><Icon icon="mi mi:warning" className="text-400"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{kpis.atRisk}</span>
                        <span className={`pd-tiny mb-1 ${kpis.needsAttention ? "tone-amber" : "text-500"}`}>{kpis.needsAttention ? `${kpis.needsAttention} کار نیازمند توجه` : "کار و مایلستون"}</span></div>
                </button>
                <button type="button" className="pd-card pd-kpi" onClick={() => onAllocation(null)}>
                    <div className="flex align-items-center justify-content-between"><span className="pd-label">تیم درگیر</span><Icon icon="mi mi:groups" className="text-400"/></div>
                    <div className="flex align-items-end gap-2 mt-2"><span className="pd-kpi-value">{kpis.team}</span>
                        <span className={`pd-tiny mb-1 ${kpis.overloaded || kpis.membersWithOverdue ? "tone-amber" : "text-500"}`}>
                            {kpis.overloaded ? `${kpis.overloaded} نفر بیش از ظرفیت` : kpis.membersWithOverdue ? `${kpis.membersWithOverdue} نفر با کار عقب‌افتاده` : "نفر با کار باز"}
                        </span></div>
                </button>
            </section>

            <section className="pd-grid">
                <AttentionCard dashboard={dashboard} onWhy={onWhy} onFollowUp={onFollowUp} canActOn={canActOn} canEditTasks={canEditTasks}
                               onReschedule={onReschedule} onAllocation={onAllocation} onAll={onOpenAttention}/>
                <ProgressCard dashboard={dashboard} range={range} onRange={onRange}/>
            </section>

            <section className="pd-grid">
                <MilestonesCard milestones={dashboard.milestones} onOpen={onOpenMilestone} onTimeline={onOpenTimeline}/>
                <TeamCard team={dashboard.team} onAllocation={onAllocation} canEditTasks={canEditTasks}/>
            </section>

            <section className="pd-grid">
                <HealthDetails dashboard={dashboard}/>
                <GoalCard goals={dashboard.goals} navigate={navigate}/>
            </section>
        </>
    );
}

function AttentionCard({dashboard, onWhy, onFollowUp, canActOn, canEditTasks, onReschedule, onAllocation, onAll}) {
    const items = dashboard.attention.items;
    return (
        <div className="pd-col-5 pd-card soft pd-card-pad">
            <div className="flex align-items-center justify-content-between mb-3">
                <div>
                    <div className="flex align-items-center gap-2"><h3 className="pd-title">نیازمند توجه شما</h3>
                        <HelpTip text="زوفینو از روی داده‌های ثبت‌شده، مواردی را که احتمال مشکل دارند شناسایی می‌کند. «در خطر» به معنی متوقف‌شدن قطعی کار نیست؛ فقط یعنی شواهدی وجود دارد که باید بررسی شود."/></div>
                    <p className="pd-sub">بر اساس تأخیر، سررسید، فعالیت، کار باقی‌مانده و وضعیت مایلستون‌ها</p>
                </div>
                <span className={`pd-pill ${items.length ? "tone-red" : "tone-green"}`}>{items.length}</span>
            </div>
            <div className="flex flex-column gap-2">
                {!items.length && (
                    <div className="pd-bg tone-green border-round-xl p-3 text-xs tone-green flex align-items-center gap-2">
                        <Icon icon="mi mi:check_circle"/>نشانه‌ای که نیاز به بررسی داشته باشد دیده نشد.
                    </div>
                )}
                {items.slice(0, 4).map((item) => {
                    const status = RISK_STATUS[item.risk.status];
                    const isProject = item.entityType === "project";
                    const isMember = item.entityType === "member";
                    return (
                        <div key={item.key} className={`pd-attention tone-${status.tone}`}>
                            <div className="flex gap-3">
                                <Icon icon={`mi mi:${isMember ? "person_alert" : isProject ? "schedule" : status.icon}`} className={`tone-${status.tone}`}/>
                                <div className="flex-1">
                                    <div className="flex align-items-center justify-content-between gap-2">
                                        <b className="text-xs">{attentionTitle(item)}</b>
                                        <span className={`pd-tiny font-bold tone-${status.tone} white-space-nowrap`}>{SEVERITY[item.risk.severity]}</span>
                                    </div>
                                    <p className="pd-tiny text-600 m-0 mt-1 line-height-3">{signalsSummary(item.risk.signals)}</p>
                                    {item.followUp && <p className="pd-tiny text-500 m-0 mt-1">{`پیگیری: ${item.followUp.owner?.name || ""} تا ${shortDate(item.followUp.dueAt)}`}</p>}
                                    <div className="flex flex-wrap align-items-center gap-2 mt-2">
                                        <button type="button" className="pd-btn small" onClick={() => onWhy(item)}>{item.risk.status === "at_risk" ? "چرا در خطر است؟" : "چرا نیازمند توجه است؟"}</button>
                                        {isProject && canEditTasks && (item.overdueTasks || []).length > 0 && <button type="button" className="pd-link" onClick={onReschedule}>بازبرنامه‌ریزی</button>}
                                        {isMember && canEditTasks && <button type="button" className="pd-link" onClick={() => onAllocation(item.entityId)}>بررسی تخصیص</button>}
                                        {canActOn(item) && !item.followUp && <button type="button" className="pd-link" onClick={() => onFollowUp(item)}>پیگیری</button>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {items.length > 4 && <button type="button" className="pd-link text-center mt-1" onClick={onAll}>{`مشاهده همه ${items.length} مورد`}</button>}
                {dashboard.attention.reviewedCount > 0 && <span className="pd-tiny text-500">{`${dashboard.attention.reviewedCount} مورد بررسی‌شده پنهان است (تا وقتی بدتر نشود)`}</span>}
            </div>
        </div>
    );
}

function ProgressCard({dashboard, range, onRange}) {
    const {progress, week, lastProgress} = dashboard;
    const chartData = useMemo(() => ({
        labels: progress.series.map((point) => shortDate(point.at)),
        datasets: [
            {label: "برنامه", data: progress.series.map((point) => point.planned), borderColor: "#d6d3d1", borderDash: [8, 6], borderWidth: 3, pointRadius: 0, tension: 0.2, spanGaps: true},
            {label: "واقعی", data: progress.series.map((point) => point.actual), borderColor: "#E6531E", borderWidth: 4, pointRadius: 0, tension: 0.2, spanGaps: false},
        ],
    }), [progress.series]);
    const chartOptions = useMemo(() => ({
        maintainAspectRatio: false,
        animation: false,
        plugins: {legend: {display: false}, tooltip: {rtl: true, titleFont: {family: CHART_FONT}, bodyFont: {family: CHART_FONT}, callbacks: {label: (item) => `${item.dataset.label}: ${item.formattedValue}٪`}}},
        scales: {
            x: {grid: {display: false}, ticks: {font: {family: CHART_FONT, size: 10}, color: "#a8a29e", maxTicksLimit: 6}},
            y: {min: 0, max: 100, grid: {color: "#f5f5f4"}, ticks: {font: {family: CHART_FONT, size: 10}, color: "#a8a29e", stepSize: 25, callback: (value) => `${value}٪`}},
        },
    }), []);
    const gap = progress.gap;
    return (
        <div className="pd-col-7 pd-card soft pd-card-pad">
            <div className="flex flex-column sm:flex-row sm:align-items-center justify-content-between gap-3 mb-3">
                <div>
                    <div className="flex align-items-center gap-2"><h3 className="pd-title">آیا پروژه طبق برنامه جلو می‌رود؟</h3>
                        <HelpTip text="خط خاکستری نشان می‌دهد طبق برنامه (موعد کارها) تا هر تاریخ چند درصد پروژه باید تکمیل می‌شد. خط نارنجی پیشرفت واقعی است. فاصله دو خط یعنی میزان جلو یا عقب بودن از برنامه."/></div>
                    <p className="pd-sub">{`خط نارنجی: پیشرفت واقعی · خط خاکستری: پیشرفت مورد انتظار طبق برنامه · ${progressMethodText(progress.method)}`}</p>
                </div>
                <div className="pd-segment">
                    {[["30", "۳۰ روز"], ["90", "۹۰ روز"], ["all", "کل پروژه"]].map(([value, label]) => (
                        <button type="button" key={value} className={range === value ? "is-active" : ""} onClick={() => onRange(value)}>{label}</button>
                    ))}
                </div>
            </div>
            <div className="pd-chart">
                <div className="pd-chart-legend">
                    <div className="flex align-items-center gap-1"><span className="pd-dot" style={{background: "#E6531E"}}/>{`واقعی: ${progress.actualInScope ?? progress.actual}٪`}</div>
                    <div className="flex align-items-center gap-1 mt-1"><span className="pd-dot" style={{background: "#d6d3d1"}}/>{progress.planned !== null ? `برنامه: ${progress.planned}٪` : "برنامه: ثبت نشده"}</div>
                </div>
                <Chart type="line" data={chartData} options={chartOptions} style={{height: "100%"}}/>
            </div>
            {progress.tasksWithoutPlan > 0 && progress.hasPlan && <p className="pd-tiny text-500 m-0 mt-2">{`${progress.tasksWithoutPlan} کار بدون موعد در مقایسه با برنامه حساب نشده است.`}</p>}
            <div className="grid m-0 mt-3 gap-3">
                <div className="col pd-mini">
                    <div className="flex align-items-center gap-1"><span className="pd-tiny text-600 font-bold">پیشرفت این هفته</span>
                        <HelpTip text="از کارهایی که موعدشان این هفته (شنبه تا جمعه) است، چه سهمی تمام شده. مثلاً اگر ۱۰ واحد کار برای این هفته برنامه داشته‌ایم و ۸ واحد تمام شده باشد، نتیجه ۸۰٪ است."/></div>
                    {week.percent === null
                        ? <p className="text-xs text-500 m-0 mt-2">کاری برای این هفته برنامه‌ریزی نشده</p>
                        : <>
                            <div className="flex align-items-end gap-2 mt-2"><b className="text-lg">{`${week.percent}٪`}</b>
                                <span className="pd-tiny text-500 mb-1">{`${week.done} از ${week.planned} ${week.unit === "hours" ? "ساعت" : "کار"} برنامه`}</span></div>
                            {week.lateOpen > 0 && <p className="pd-tiny tone-amber m-0 mt-1">{`${week.lateOpen} کار با موعد این هفته از موعد گذشته و هنوز باز است`}</p>}
                        </>}
                </div>
                <div className={`col pd-mini ${gap > 0 ? "tone-red" : ""}`}>
                    <div className="flex align-items-center gap-1"><span className="pd-tiny text-600 font-bold">وضعیت نسبت به برنامه</span>
                        <HelpTip text="پیشرفت واقعی را با پیشرفت مورد انتظار طبق برنامه مقایسه می‌کند. عدد، اختلاف به واحد درصد است."/></div>
                    {gap === null
                        ? <p className="text-xs text-500 m-0 mt-2">موعد کافی برای مقایسه ثبت نشده</p>
                        : <>
                            <div className="flex align-items-end gap-2 mt-2"><b className={`text-lg ${gap > 0 ? "tone-red" : "tone-green"}`}>{`${Math.abs(gap)}٪`}</b>
                                <span className={`pd-tiny mb-1 ${gap > 0 ? "tone-red" : "tone-green"}`}>{gap > 0 ? "عقب‌تر از برنامه" : gap < 0 ? "جلوتر از برنامه" : "طبق برنامه"}</span></div>
                            <p className="pd-tiny text-600 m-0 mt-1">{`واقعی ${progress.actualInScope}٪ · برنامه ${progress.planned}٪`}</p>
                        </>}
                </div>
                <div className="col pd-mini">
                    <div className="flex align-items-center gap-1"><span className="pd-tiny text-600 font-bold">آخرین پیشرفت ثبت‌شده</span>
                        <HelpTip text="آخرین زمانی که کاری یا مایلستونی تمام شده. پیام، کامنت یا ویرایش ساده «پیشرفت» محسوب نمی‌شود."/></div>
                    {lastProgress
                        ? <>
                            <b className="text-sm block mt-2">{whenText(lastProgress.at)}</b>
                            <p className="pd-tiny text-600 m-0 mt-1">
                                {[lastProgress.tasksDone ? `تکمیل ${lastProgress.tasksDone} کار` : null, lastProgress.milestonesDone ? `${lastProgress.milestonesDone} مایلستون` : null, lastProgress.progressDelta ? `+${lastProgress.progressDelta}٪ پیشرفت پروژه` : null].filter(Boolean).join(" · ")}
                            </p>
                        </>
                        : <p className="text-xs text-500 m-0 mt-2">هنوز کاری تمام نشده</p>}
                </div>
            </div>
        </div>
    );
}

function MilestonesCard({milestones, onOpen, onTimeline}) {
    return (
        <div className="pd-col-7 pd-card pd-card-pad">
            <div className="flex align-items-center justify-content-between mb-2">
                <div><h3 className="pd-title">مسیر تحویل و مایلستون‌ها</h3><p className="pd-sub">آنچه باید تحویل شود، نه فقط درصد تکمیل کارها</p></div>
                <button type="button" className="pd-link" onClick={onTimeline}>برنامه زمانی</button>
            </div>
            {!milestones.length && <p className="text-xs text-500 m-0 mt-2">مایلستونی برای این پروژه ثبت نشده؛ با تعریف مایلستون‌ها، مسیر تحویل اینجا نمایش داده می‌شود.</p>}
            {milestones.slice(0, 6).map((row) => {
                const done = row.status === "done";
                const risky = row.risk.status !== "normal";
                const tone = done ? "green" : risky ? RISK_STATUS[row.risk.status].tone : "gray";
                const due = row.dueAt ? `سررسید ${shortDate(row.dueAt)}` : "بدون موعد";
                return (
                    <div key={row.documentId} className="pd-milestone" onClick={() => onOpen(row.documentId)}>
                        <span className={`pd-round pd-bg tone-${tone}`}><Icon icon={`mi mi:${done ? "check" : risky ? "hourglass_top" : "radio_button_unchecked"}`} className={`tone-${tone}`}/></span>
                        <div>
                            <b className="text-xs">{row.title}</b>
                            {done
                                ? <p className="pd-tiny text-500 m-0 mt-1">{`تحویل شده${row.doneAt ? ` · ${shortDate(row.doneAt)}` : ""}`}</p>
                                : <>
                                    <div className="pd-bar mt-2"><span style={{width: `${row.percent}%`}}/></div>
                                    <p className={`pd-tiny m-0 mt-1 ${risky ? `tone-${tone}` : "text-500"}`}>{`${row.percent}٪ · ${due} · ${row.openTasks} کار باز`}</p>
                                </>}
                        </div>
                        <span className={`pd-pill tone-${tone}`}>{done ? MILESTONE_STATUS.done : risky ? RISK_STATUS[row.risk.status].label : MILESTONE_STATUS[row.status] || MILESTONE_STATUS.todo}</span>
                    </div>
                );
            })}
            {milestones.some((row) => row.dueAt) && <p className="pd-tiny text-400 m-0 mt-2">سررسید هر مایلستون، دیرترین موعد کارهای متصل به آن است.</p>}
        </div>
    );
}

function TeamCard({team, onAllocation, canEditTasks}) {
    const maxOpen = Math.max(1, ...team.map((row) => row.open));
    return (
        <div className="pd-col-5 pd-card pd-card-pad">
            <div className="flex align-items-center justify-content-between mb-3">
                <div><h3 className="pd-title">ظرفیت و کار تیم</h3><p className="pd-sub">کارهای باز هر نفر در این پروژه؛ درصد ظرفیت فقط اگر ظرفیت عضو ثبت شده باشد</p></div>
                {canEditTasks && team.length > 0 && <button type="button" className="pd-link" onClick={() => onAllocation(null)}>بررسی تخصیص</button>}
            </div>
            {!team.length && <p className="text-xs text-500 m-0">کار بازی به کسی سپرده نشده است.</p>}
            <div className="flex flex-column gap-3">
                {team.slice(0, 6).map((row) => {
                    const over = row.load !== null && row.load > 100;
                    const width = row.load !== null ? Math.min(100, row.load) : Math.round((row.open / maxOpen) * 100);
                    const tone = over || row.overdue >= 3 ? "red" : row.load !== null ? "green" : "blue";
                    return (
                        <div key={row.person?.documentId} className="flex align-items-center gap-3">
                            {row.person?.member && <Avatar memberData={row.person.member} size="normal"/>}
                            <div className="flex-1">
                                <div className="flex justify-content-between pd-label"><b className="text-700">{row.person?.name}</b>
                                    <span className={over ? "tone-red font-bold" : "text-500"}>{row.load !== null ? `${row.load}٪ ظرفیت` : `${row.open} کار باز`}</span></div>
                                <div className="pd-bar mt-2"><span className={`tone-${tone}`} style={{width: `${width}%`}}/></div>
                                {(row.overdue > 0 || over || row.dueThisWeek > 0) && (
                                    <p className={`pd-tiny m-0 mt-1 ${over || row.overdue ? "tone-red" : "text-500"}`}>
                                        {[over ? "بیش از ظرفیت" : null, row.overdue ? `${row.overdue} عقب‌افتاده` : null, row.dueThisWeek ? `${row.dueThisWeek} موعد این هفته` : null].filter(Boolean).join(" · ")}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function HealthDetails({dashboard}) {
    const {readiness, forecast, health, kpis} = dashboard;
    const timeSignals = health.signals.filter((signal) => ["schedule_gap", "project_overdue", "risky_tasks", "risky_milestones"].includes(signal.type));
    const timeLevel = timeSignals.reduce((sum, signal) => sum + signal.weight, 0);
    const missing = readiness.checks.filter((check) => !check.ok);
    return (
        <details className="pd-col-7 pd-card" style={{overflow: "hidden"}}>
            <summary className="pd-card-pad cursor-pointer flex align-items-center justify-content-between" style={{listStyle: "none"}}>
                <div><h3 className="pd-title">سلامت، ریسک و آمادگی پروژه</h3><p className="pd-sub">جزئیات تشخیصی برای مدیر پروژه</p></div>
                <Icon icon="mi mi:expand_more" className="text-400"/>
            </summary>
            <div className="px-3 pb-3 grid m-0 gap-2">
                <div className="col pd-mini" style={{minWidth: "9rem"}}>
                    <span className="pd-tiny text-500">آمادگی و وضوح پروژه</span>
                    <b className="text-lg block mt-1">{`${readiness.percent}٪`}</b>
                    <span className={`pd-tiny ${missing.length ? "tone-amber" : "tone-green"}`}>{missing.length ? `ثبت نشده: ${missing.map((check) => READINESS_LABELS[check.key]).join("، ")}` : "کامل تعریف شده"}</span>
                </div>
                <div className="col pd-mini" style={{minWidth: "9rem"}}>
                    <span className="pd-tiny text-500">ریسک زمان</span>
                    <b className={`text-sm block mt-2 ${timeLevel >= 50 ? "tone-red" : timeLevel >= 25 ? "tone-amber" : "tone-green"}`}>{timeLevel >= 50 ? "بالا" : timeLevel >= 25 ? "متوسط" : "کم"}</b>
                    <span className="pd-tiny text-500">{timeSignals.length ? `${timeSignals.length} عامل فعال` : "عاملی دیده نشد"}</span>
                </div>
                <div className="col pd-mini" style={{minWidth: "9rem"}}>
                    <span className="pd-tiny text-500">ریسک منابع</span>
                    <b className={`text-sm block mt-2 ${kpis.overloaded ? "tone-red" : kpis.membersWithOverdue ? "tone-amber" : "tone-green"}`}>{kpis.overloaded ? "بالا" : kpis.membersWithOverdue ? "متوسط" : "کم"}</b>
                    <span className="pd-tiny text-500">{kpis.overloaded ? `${kpis.overloaded} نفر بیش از ظرفیت` : kpis.membersWithOverdue ? `${kpis.membersWithOverdue} نفر با کار عقب‌افتاده` : "بار کاری عادی"}</span>
                </div>
                <div className="col pd-mini" style={{minWidth: "9rem"}}>
                    <span className="pd-tiny text-500">کیفیت داده برای تحلیل</span>
                    <b className="text-lg block mt-1">{`${forecast.confidence}٪`}</b>
                    <span className="pd-tiny text-500">{`موعد ${forecast.coverage?.dated ?? 0}٪ · برآورد ${forecast.coverage?.estimated ?? 0}٪ · مسئول ${forecast.coverage?.assigned ?? 0}٪`}</span>
                </div>
            </div>
        </details>
    );
}

function GoalCard({goals, navigate}) {
    return (
        <div className="pd-col-5 pd-card pd-card-pad">
            <h3 className="pd-title">ارتباط با هدف</h3>
            <p className="pd-sub">این پروژه چرا باید ادامه پیدا کند؟</p>
            {!goals.length && <p className="text-xs text-500 m-0 mt-3">این پروژه به هدفی متصل نیست.</p>}
            {goals.slice(0, 2).map((goal) => (
                <div key={goal.documentId} className="mt-3 border-round-xl p-3" style={{border: "1px solid #ffedd5", background: "rgba(255, 247, 237, 0.4)"}}>
                    <div className="flex align-items-center gap-2"><Icon icon="mi mi:flag" style={{color: "var(--primary-color)"}}/><b className="text-xs">{goal.title}</b></div>
                    <div className="flex align-items-center justify-content-between mt-3 pd-label">
                        <span>{`پیشرفت هدف${goal.goalStatus ? ` · ${GOAL_STATUS[goal.goalStatus] || ""}` : ""}`}</span><b>{goal.progress !== null ? `${goal.progress}٪` : "—"}</b>
                    </div>
                    <div className="pd-bar mt-2" style={{background: "#fff"}}><span style={{width: `${goal.progress || 0}%`}}/></div>
                    <p className="pd-tiny text-500 m-0 mt-2">{goal.projects > 1 ? `این پروژه یکی از ${goal.projects} پروژه این هدف است` : "تنها پروژه این هدف است"}</p>
                    <button type="button" className="pd-link mt-2" onClick={() => navigate(`/goals/${goal.id}`)}>مشاهده هدف</button>
                </div>
            ))}
        </div>
    );
}

// ---------- «کار من» ----------
function MemberView({dashboard, onOpenTask, onWhy, onCloseFollowUp}) {
    const {tasks, stats, followUps} = dashboard.myWork;
    const focusHours = Math.round((stats.focusSeconds / 3600) * 10) / 10;
    const itemFor = (task) => ({entityType: "task", entityId: task.documentId, title: task.title, risk: task.risk, task});
    return (
        <section className="pd-grid">
            <div className="pd-col-8 pd-card pd-card-pad">
                <div className="flex align-items-center justify-content-between mb-3">
                    <div><h3 className="pd-title">کار من در این پروژه</h3><p className="pd-sub">به ترتیب آنچه الان باید انجام دهید</p></div>
                    <span className="pd-pill tone-amber">{`${stats.open} کار باز`}</span>
                </div>
                {!tasks.length && <p className="text-xs text-500 m-0">در این پروژه کار بازی به شما سپرده نشده است.</p>}
                <div className="flex flex-column gap-2">
                    {tasks.map((task) => {
                        const days = daysUntil(task.dueDate);
                        const late = days !== null && new Date(task.dueDate).getTime() < Date.now();
                        const risky = task.risk.status !== "normal";
                        return (
                            <div key={task.documentId} className={`pd-my-task ${late || task.risk.status === "at_risk" ? "tone-red" : ""}`}>
                                <div className="flex-1">
                                    <div className="flex flex-wrap align-items-center gap-2">
                                        <b className="text-xs">{task.title}</b>
                                        {late && <span className="pd-pill tone-red">عقب‌افتاده</span>}
                                        {!late && days === 0 && <span className="pd-pill tone-red">امروز</span>}
                                        {risky && <span className={`pd-pill tone-${RISK_STATUS[task.risk.status].tone}`}>{RISK_STATUS[task.risk.status].label}</span>}
                                    </div>
                                    <p className="pd-tiny text-600 m-0 mt-1">
                                        {[task.dueDate ? `موعد ${shortDate(task.dueDate)} (${daysText(days)})` : "بدون موعد", task.milestone ? `مایلستون: ${task.milestone.title}` : null, task.estimateMinutes ? `برآورد ${Math.round(task.estimateMinutes / 6) / 10} ساعت` : null].filter(Boolean).join(" · ")}
                                    </p>
                                    {risky && <button type="button" className="pd-link mt-2" onClick={() => onWhy(itemFor(task))}>{task.risk.status === "at_risk" ? "چرا در خطر است؟" : "چرا نیازمند توجه است؟"}</button>}
                                </div>
                                <button type="button" className="pd-link" onClick={() => onOpenTask(task.documentId)}>باز کردن</button>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="pd-col-4 flex flex-column gap-3">
                <div className="pd-card pd-card-pad">
                    <h3 className="pd-title">وضعیت من</h3>
                    <div className="grid m-0 mt-2 gap-2">
                        <div className="col-5 pd-stat pd-bg tone-amber flex-grow-1"><span className="pd-tiny tone-amber">در حال انجام</span><b className="text-xl block">{stats.doing}</b></div>
                        <div className="col-5 pd-stat pd-bg tone-red flex-grow-1"><span className="pd-tiny tone-red">عقب‌افتاده</span><b className="text-xl block">{stats.overdue}</b></div>
                        <div className="col-5 pd-stat pd-bg tone-green flex-grow-1"><span className="pd-tiny tone-green">این هفته تکمیل</span><b className="text-xl block">{stats.doneThisWeek}</b></div>
                        <div className="col-5 pd-stat pd-bg tone-blue flex-grow-1"><span className="pd-tiny tone-blue">تمرکز این هفته</span><b className="text-xl block">{`${focusHours} ساعت`}</b></div>
                    </div>
                </div>
                <div className="pd-card pd-card-pad">
                    <h3 className="pd-title">پیگیری‌های من</h3>
                    <p className="pd-sub">مواردی که بررسی آن‌ها به شما سپرده شده</p>
                    {!followUps.length && <p className="text-xs text-500 m-0 mt-3">پیگیری بازی ندارید.</p>}
                    <div className="flex flex-column gap-3 mt-3">
                        {followUps.map((followUp) => (
                            <div key={followUp.documentId} className="flex gap-2">
                                <Icon icon="mi mi:assignment_ind" className="tone-amber text-lg"/>
                                <div className="flex-1">
                                    <b className="text-xs">{followUp.entityTitle}</b>
                                    <p className="pd-tiny text-500 m-0 mt-1">{`تا ${fullDate(followUp.dueAt)}${followUp.createdBy?.name ? ` · از طرف ${followUp.createdBy.name}` : ""}`}</p>
                                    {followUp.note && <p className="pd-tiny text-600 m-0 mt-1">{followUp.note}</p>}
                                    <button type="button" className="pd-link mt-1" onClick={() => onCloseFollowUp(followUp)}>انجام شد</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
